import React, { useCallback, useEffect, useRef, useState } from 'react';

interface PromptBannerProps {
  /** Friendly per-turn preamble (from JSON field "message"). Streams in token-by-token. */
  preamble?: string | null;
  /** Clarifying question (from JSON field "prompt"). Streams in after the preamble. */
  question?: string | null;
  /** Loading state (classify-intent or product-search). Loading dots show only while no text has streamed yet. */
  isLoading?: boolean;
  /** Parse error UI (handled by parent). */
  parseError?: boolean;
  /** Optional retry handler for parse errors (rendered by parent). */
  errorSlot?: React.ReactNode;
  /** Variant: "above" centers for above-the-searchbar banner; "inline" is the topMenu desktop panel variant. */
  variant?: 'above' | 'inline';
  /** Fired once when the question typewriter completes for this turn. Used to reveal the suggestion chips below. */
  onQuestionTypewriterComplete?: () => void;
  /**
   * Identifier of the current assistant turn (e.g. the latest assistant message id).
   * When this changes, the component resets its typewriter state so a fresh turn starts cleanly.
   */
  turnId?: string;
  /** First product-search question keeps a longer preamble hold; follow-ups reveal the question sooner. */
  isFirstTurn?: boolean;
  /**
   * Render this turn fully-formed with no typewriter replay (restored/resumed turns).
   * The question appears complete, the preamble is already dismissed, and the
   * completion callback fires immediately so the chips are there right away.
   */
  instant?: boolean;
}

// Tuning constants — keep terse so they're easy to spot
const PREAMBLE_SPEED_MS = 8;           // typing speed for the friendly preamble (very fast — feels alive)
const QUESTION_SPEED_MS = 12;          // typing speed for the clarifying question (snappy utility)
const PREAMBLE_MIN_VISIBLE_MS_FIRST = 2300;    // first turn: let the friendly preamble breathe before the question
const PREAMBLE_MIN_VISIBLE_MS_FOLLOWUP = 900;  // follow-up turns: snappier transition to the next question
const QUESTION_STABILIZATION_MS = 120; // wait after the question typewriter catches up to confirm the text is final
const PREAMBLE_EXIT_MS = 500;          // slide-up + fade duration for the preamble once the question is fully shown (matches duration-500)
const PREAMBLE_PEEK_MS = 2800;         // how long a scroll-triggered preamble peek stays visible before dismissing again
const TOUCH_PEEK_DELTA_PX = 24;        // finger travel needed to treat a touchmove as "scroll up" for peek
const MOBILE_BREAKPOINT_PX = 640;      // Tailwind `sm` — matches isMobileView in text-area-search-bar
const MOBILE_PREAMBLE_EARLY_DISMISS_MS = 150; // mobile only: dismiss the preamble this soon after the question starts typing (vertical space is scarce above the search bar)

/**
 * Two-phase client-side typewriter.
 *
 *   phase = preamble
 *     - reveals `preamble` character-by-character at PREAMBLE_SPEED_MS.
 *     - when the typewriter catches up AND `question` has started streaming
 *       AND the preamble has been visible for at least the turn-specific min hold,
 *       we enter the question phase. The fully-typed preamble stays on screen.
 *
 *   phase = question
 *     - the completed preamble remains visible at the top; `question` types in
 *       below it character-by-character at QUESTION_SPEED_MS.
 *     - when fully revealed AND text has been stable for QUESTION_STABILIZATION_MS,
 *       the preamble slides up and fades out (PREAMBLE_EXIT_MS), then unmounts so
 *       only the question remains, and `onQuestionTypewriterComplete()` fires.
 *       Parent uses that to reveal the suggestion chips below the search bar.
 *     - on mobile (viewport < MOBILE_BREAKPOINT_PX), the preamble's slide-up/fade
 *       is triggered early — MOBILE_PREAMBLE_EARLY_DISMISS_MS after the question
 *       starts typing, rather than waiting for it to finish — because vertical
 *       space above the search bar is scarce. This only moves the visual dismiss;
 *       `onQuestionTypewriterComplete()` still fires on full reveal, so the
 *       suggestion chips appear at the same time as on desktop.
 *     - after the preamble has dismissed, a scroll-up / swipe in this banner briefly
 *       peeks it back above the question for PREAMBLE_PEEK_MS, then dismisses again.
 *       Peek only intercepts the gesture when the panel actually overflows; if the
 *       question + chips already fit, scroll-up belongs to the page.
 *
 * Loading dots only render when isLoading is true AND no text has streamed yet
 * AND no LOADING_TO_TEXT_GRACE_MS delay is in effect. As soon as the first
 * character of the preamble arrives, the typewriter takes over.
 */
export function PromptBanner({
  preamble,
  question,
  isLoading,
  parseError,
  errorSlot,
  variant = 'above',
  onQuestionTypewriterComplete,
  turnId,
  isFirstTurn = true,
  instant = false,
}: PromptBannerProps) {
  const preambleMinVisibleMs = isFirstTurn
    ? PREAMBLE_MIN_VISIBLE_MS_FIRST
    : PREAMBLE_MIN_VISIBLE_MS_FOLLOWUP;
  // Always start in preamble phase. The existing effect below handles the
  // "preamble never arrives but question does" fallback. Initializing to
  // `question` based on the first render's `preamble` value is a footgun:
  // because the empty assistant message arrives ~10ms BEFORE the preamble
  // text streams in, we would lock into `question` phase forever and never
  // show the preamble at all.
  const [phase, setPhase] = useState<'preamble' | 'question'>('preamble');
  const [visibleChars, setVisibleChars] = useState(0);
  // In question phase the preamble lingers above the question. `dismissPreamble`
  // triggers its slide-up/fade; `preambleGone` unmounts it once that finishes.
  const [dismissPreamble, setDismissPreamble] = useState(false);
  const [preambleGone, setPreambleGone] = useState(false);
  // Mobile gets an earlier preamble dismiss (see MOBILE_PREAMBLE_EARLY_DISMISS_MS).
  // Starts false so SSR/first paint matches desktop; corrected on mount.
  const [isMobile, setIsMobile] = useState(false);

  const rootRef = useRef<HTMLDivElement>(null);
  const preambleStartedAtRef = useRef<number | null>(null);
  const holdTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const stabilizeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dismissTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const peekTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const touchStartYRef = useRef<number | null>(null);
  const completedRef = useRef(false);
  const previousTurnIdRef = useRef<string | undefined>(turnId);
  const preambleGoneRef = useRef(false);
  const dismissPreambleRef = useRef(false);
  // Once the preamble has dismissed at least once this turn, scroll can peek it back.
  const [canPeek, setCanPeek] = useState(false);

  // Keep refs in sync so event listeners can read the latest dismiss state without re-binding.
  useEffect(() => {
    preambleGoneRef.current = preambleGone;
  }, [preambleGone]);
  useEffect(() => {
    dismissPreambleRef.current = dismissPreamble;
  }, [dismissPreamble]);

  // Track mobile viewport so the question phase can dismiss the preamble early
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT_PX);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // Reset typewriter state whenever the active turn changes
  useEffect(() => {
    if (turnId !== previousTurnIdRef.current) {
      previousTurnIdRef.current = turnId;
      setPhase('preamble');
      setVisibleChars(0);
      setDismissPreamble(false);
      setPreambleGone(false);
      setCanPeek(false);
      preambleStartedAtRef.current = null;
      completedRef.current = false;
      touchStartYRef.current = null;
      if (holdTimerRef.current) {
        clearTimeout(holdTimerRef.current);
        holdTimerRef.current = null;
      }
      if (stabilizeTimerRef.current) {
        clearTimeout(stabilizeTimerRef.current);
        stabilizeTimerRef.current = null;
      }
      if (dismissTimerRef.current) {
        clearTimeout(dismissTimerRef.current);
        dismissTimerRef.current = null;
      }
      if (peekTimerRef.current) {
        clearTimeout(peekTimerRef.current);
        peekTimerRef.current = null;
      }
    }
  }, [turnId]);

  useEffect(() => {
    if (preambleGone) setCanPeek(true);
  }, [preambleGone]);

  // Restored turns (resume) render fully-formed — replaying the typewriter on a
  // question the user already read reads as a stutter, not as life. Jump straight
  // to the finished question-phase state and fire the completion callback so the
  // chips are present immediately. Declared AFTER the turn-change reset effect so
  // a turn change resets first and this re-applies in the same commit.
  useEffect(() => {
    if (!instant) return;
    const full = (question ?? '').length;
    if (completedRef.current && phase === 'question' && visibleChars >= full) return;
    setPhase('question');
    setVisibleChars(full);
    setDismissPreamble(true);
    setPreambleGone(true);
    if (!completedRef.current) {
      completedRef.current = true;
      onQuestionTypewriterComplete?.();
    }
  }, [instant, question, turnId, phase, visibleChars, onQuestionTypewriterComplete]);

  const schedulePeekDismiss = useCallback(() => {
    if (peekTimerRef.current) clearTimeout(peekTimerRef.current);
    peekTimerRef.current = setTimeout(() => {
      setDismissPreamble(true);
      peekTimerRef.current = null;
    }, PREAMBLE_PEEK_MS);
  }, []);

  /** Briefly re-show the preamble above the question after it has already dismissed. */
  const peekPreamble = useCallback(() => {
    if (!preamble) return;
    // Already fully visible (peeking) — refresh the auto-hide timer.
    if (!preambleGoneRef.current && !dismissPreambleRef.current) {
      schedulePeekDismiss();
      return;
    }
    if (dismissTimerRef.current) {
      clearTimeout(dismissTimerRef.current);
      dismissTimerRef.current = null;
    }
    setPreambleGone(false);
    setDismissPreamble(false);
    schedulePeekDismiss();
  }, [preamble, schedulePeekDismiss]);

  // After the first dismiss, scroll-up / swipe in the top panel peeks the preamble back briefly
  // — but only when the panel is actually a scroller. Prefer the AboveBarPromptPanel glass
  // surface so the whole top section is the hit target.
  useEffect(() => {
    if (phase !== 'question') return;
    if (!preamble) return;
    if (!canPeek) return;

    const root = rootRef.current;
    if (!root) return;
    const el =
      (root.closest('[data-above-bar-panel]') as HTMLElement | null) ?? root;

    const onWheel = (e: WheelEvent) => {
      // Negative deltaY = scroll up (reveal content above).
      if (e.deltaY >= 0) return;
      // Question + chips already fit in the panel — there is nothing to reveal.
      // Stealing the wheel here hijacks page scroll over a large glass surface.
      if (el.scrollHeight - el.clientHeight <= 1) return;
      // Preamble already on screen — leave native overflow scroll alone so it stays
      // reachable in the panel; only refresh the peek auto-hide if we're mid-peek.
      if (!preambleGoneRef.current) {
        if (peekTimerRef.current) schedulePeekDismiss();
        return;
      }
      e.preventDefault();
      peekPreamble();
    };

    const onTouchStart = (e: TouchEvent) => {
      touchStartYRef.current = e.touches[0]?.clientY ?? null;
    };

    const onTouchMove = (e: TouchEvent) => {
      const startY = touchStartYRef.current;
      const currentY = e.touches[0]?.clientY;
      if (startY == null || currentY == null) return;
      if (el.scrollHeight - el.clientHeight <= 1) return;
      // Finger moving down → content scrolls up → peek the preamble that lived above.
      if (currentY - startY < TOUCH_PEEK_DELTA_PX) return;
      touchStartYRef.current = currentY;
      peekPreamble();
    };

    const onTouchEnd = () => {
      touchStartYRef.current = null;
    };

    el.addEventListener('wheel', onWheel, { passive: false });
    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: true });
    el.addEventListener('touchend', onTouchEnd, { passive: true });
    return () => {
      el.removeEventListener('wheel', onWheel);
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
    };
  }, [phase, preamble, canPeek, peekPreamble, schedulePeekDismiss]);

  // Record the moment the preamble first becomes visible so we can enforce min hold
  useEffect(() => {
    if (preamble && preambleStartedAtRef.current === null) {
      preambleStartedAtRef.current = Date.now();
    }
  }, [preamble]);

  // If we started without any preamble but a question arrives, jump straight to question phase
  useEffect(() => {
    if (phase === 'preamble' && !preamble && question) {
      setPhase('question');
      setVisibleChars(0);
    }
  }, [phase, preamble, question]);

  const currentText = phase === 'preamble' ? (preamble ?? '') : (question ?? '');
  const currentSpeed = phase === 'preamble' ? PREAMBLE_SPEED_MS : QUESTION_SPEED_MS;

  // Typewriter tick — advance one character at a time
  useEffect(() => {
    if (!currentText) return;
    if (visibleChars >= currentText.length) return;
    const t = setTimeout(() => setVisibleChars((v) => v + 1), currentSpeed);
    return () => clearTimeout(t);
  }, [currentText, visibleChars, currentSpeed]);

  // Preamble → question transition (gated by min visible time)
  useEffect(() => {
    if (phase !== 'preamble') return;
    if (!preamble) return;
    if (visibleChars < preamble.length) {
      if (holdTimerRef.current) {
        clearTimeout(holdTimerRef.current);
        holdTimerRef.current = null;
      }
      return;
    }
    if (!question) {
      if (holdTimerRef.current) {
        clearTimeout(holdTimerRef.current);
        holdTimerRef.current = null;
      }
      return;
    }
    // Caught up AND question is here. Enforce min visible time before swap.
    const startedAt = preambleStartedAtRef.current ?? Date.now();
    const elapsed = Date.now() - startedAt;
    const remaining = Math.max(0, preambleMinVisibleMs - elapsed);
    if (holdTimerRef.current) clearTimeout(holdTimerRef.current);
    if (remaining === 0) {
      setPhase('question');
      setVisibleChars(0);
    } else {
      holdTimerRef.current = setTimeout(() => {
        setPhase('question');
        setVisibleChars(0);
      }, remaining);
    }
    return () => {
      if (holdTimerRef.current) {
        clearTimeout(holdTimerRef.current);
        holdTimerRef.current = null;
      }
    };
  }, [phase, preamble, visibleChars, question, preambleMinVisibleMs]);

  // Question phase — stabilize, then fire onComplete
  useEffect(() => {
    if (phase !== 'question') return;
    if (!currentText) return;
    if (visibleChars < currentText.length) {
      if (stabilizeTimerRef.current) {
        clearTimeout(stabilizeTimerRef.current);
        stabilizeTimerRef.current = null;
      }
      return;
    }
    if (completedRef.current) return;
    if (stabilizeTimerRef.current) clearTimeout(stabilizeTimerRef.current);
    stabilizeTimerRef.current = setTimeout(() => {
      if (!completedRef.current) {
        completedRef.current = true;
        setDismissPreamble(true);
        onQuestionTypewriterComplete?.();
      }
    }, QUESTION_STABILIZATION_MS);
    return () => {
      if (stabilizeTimerRef.current) {
        clearTimeout(stabilizeTimerRef.current);
        stabilizeTimerRef.current = null;
      }
    };
  }, [phase, currentText, visibleChars, onQuestionTypewriterComplete]);

  // Mobile only: start the preamble's slide-up/fade shortly after the question
  // phase begins, rather than waiting for the question to finish typing — vertical
  // space above the search bar is scarce on mobile. This moves only the visual
  // dismiss; onQuestionTypewriterComplete (which reveals the suggestion chips)
  // still fires from the stabilization effect above on full reveal.
  // visibleChars is deliberately omitted from deps so per-character ticks don't
  // re-arm the timer — it anchors to the moment the question phase begins.
  // Skip once canPeek is true so scroll-triggered peeks aren't cut short by this
  // 150ms early-dismiss path (the peek timer owns re-hide instead).
  useEffect(() => {
    if (!isMobile) return;
    if (phase !== 'question') return;
    if (dismissPreamble) return;
    if (canPeek) return;
    const t = setTimeout(() => setDismissPreamble(true), MOBILE_PREAMBLE_EARLY_DISMISS_MS);
    return () => clearTimeout(t);
  }, [isMobile, phase, dismissPreamble, canPeek]);

  // Once the preamble starts sliding up, unmount it after the exit animation so
  // only the question is left occupying the slot.
  useEffect(() => {
    if (!dismissPreamble) return;
    dismissTimerRef.current = setTimeout(() => setPreambleGone(true), PREAMBLE_EXIT_MS);
    return () => {
      if (dismissTimerRef.current) {
        clearTimeout(dismissTimerRef.current);
        dismissTimerRef.current = null;
      }
    };
  }, [dismissPreamble]);

  // Cleanup all timers on unmount
  useEffect(
    () => () => {
      if (holdTimerRef.current) clearTimeout(holdTimerRef.current);
      if (stabilizeTimerRef.current) clearTimeout(stabilizeTimerRef.current);
      if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
      if (peekTimerRef.current) clearTimeout(peekTimerRef.current);
    },
    [],
  );

  if (parseError) {
    return <>{errorSlot}</>;
  }

  // Dots only while we genuinely have nothing to show yet
  const showLoadingDots = isLoading && !preamble && !question;
  if (showLoadingDots) {
    return (
      <div
        className={
          variant === 'above'
            ? 'flex items-center justify-center gap-1.5 py-1'
            : 'flex items-center justify-center gap-1.5'
        }
      >
        <span className="h-2 w-2 animate-pulse rounded-full bg-cornflowerblue/70" style={{ animationDelay: '0ms' }} />
        <span className="h-2 w-2 animate-pulse rounded-full bg-cornflowerblue/70" style={{ animationDelay: '150ms' }} />
        <span className="h-2 w-2 animate-pulse rounded-full bg-cornflowerblue/70" style={{ animationDelay: '300ms' }} />
      </div>
    );
  }

  if (!currentText && phase === 'preamble') return null;

  const textClass =
    variant === 'above'
      ? 'm-auto max-w-[80%] overflow-hidden text-center max-sm:leading-tight text-lg text-gray-600 dark:text-white'
      : 'm-auto max-w-[80%] overflow-hidden text-center text-lg text-gray-600 dark:text-white';

  // Question phase: keep the finished preamble above while the question types in
  // below. When the question completes, the preamble slides up + fades, then unmounts.
  // After dismiss, scroll-up on this container briefly peeks the preamble back.
  //
  // `data-pin-anchor` tells AboveBarPromptPanel which element its scroll pin must
  // keep visible when the content overflows the height clamp: normally the
  // question — it must NEVER end up hidden above the fold with only the chips
  // showing. During a scroll-triggered peek (canPeek && preamble remounted) the
  // preamble takes the anchor instead, since showing it is the whole point of
  // the peek.
  if (phase === 'question') {
    const peekActive = canPeek && !!preamble && !preambleGone;
    return (
      <div ref={rootRef} className="flex w-full flex-col items-center gap-2">
        {preamble && !preambleGone && (
          <p
            data-pin-anchor={peekActive ? '' : undefined}
            className={`${textClass} transition-all duration-500 ease-out ${
              dismissPreamble ? '-translate-y-3 opacity-0' : 'translate-y-0 opacity-100'
            }`}
          >
            {preamble}
          </p>
        )}
        <p data-pin-anchor={peekActive ? undefined : ''} className={textClass}>
          {(question ?? '').substring(0, visibleChars)}
        </p>
      </div>
    );
  }

  return (
    <p key={phase} className={textClass}>
      {currentText.substring(0, visibleChars)}
    </p>
  );
}
